import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API, { getMediaURL } from '../../../context/API';

export interface MerchantOption {
  id: string;
  name: string;
}

export interface MerchantCategory {
  id: string;
  category: string;
  picture: string | null;
  merchant: string[];
  /** Preserved from API for display (names in selector) */
  merchantDetails?: { id: string; name: string }[];
  sequence_number: number;
  items?: unknown[];
}

export interface MerchantCategoriesState {
  merchants: MerchantOption[];
  categories: MerchantCategory[];
  categoryOrder: string[];
  loading: {
    merchants: boolean;
    categories: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    reorder: boolean;
  };
  error: string | null;
}

const initialState: MerchantCategoriesState = {
  merchants: [],
  categories: [],
  categoryOrder: [],
  loading: {
    merchants: false,
    categories: false,
    create: false,
    update: false,
    delete: false,
    reorder: false,
  },
  error: null,
};

const mapCategoryFromApi = (category: {
  picture?: string;
  merchant?: (string | { id: string; name?: string })[];
} & MerchantCategory): MerchantCategory => {
  const merchantList = category.merchant ?? [];
  const ids = merchantList.map((m) => (typeof m === 'string' ? m : m.id));
  const details: { id: string; name: string }[] = [];
  merchantList.forEach((m) => {
    if (typeof m === 'object' && m != null && 'id' in m && 'name' in m && m.name) {
      details.push({ id: m.id, name: String(m.name) });
    }
  });
  return {
    ...category,
    picture: category.picture ? getMediaURL(category.picture) : null,
    merchant: ids,
    merchantDetails: details.length > 0 ? details : undefined,
  };
};

export const fetchMerchantsForCategories = createAsyncThunk<
  MerchantOption[],
  void,
  { rejectValue: string }
>('merchantCategories/fetchMerchants', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ success?: boolean; data?: MerchantOption[] }>('/merchant');
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to fetch merchants');
  }
});

export const fetchMerchantCategories = createAsyncThunk<
  MerchantCategory[],
  void,
  { rejectValue: string }
>('merchantCategories/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get<{ data?: unknown[] }>('/merchant-categories');
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    const mapped = list.map((c) => mapCategoryFromApi(c as Parameters<typeof mapCategoryFromApi>[0]));
    mapped.sort((a, b) => a.sequence_number - b.sequence_number);
    return mapped;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to fetch categories');
  }
});

export const createMerchantCategory = createAsyncThunk<
  MerchantCategory,
  FormData,
  { rejectValue: string }
>('merchantCategories/create', async (formData, { rejectWithValue }) => {
  try {
    const response = await API.post<{ data?: MerchantCategory }>('/merchant-categories', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const data = response.data?.data ?? response.data;
    return data ? mapCategoryFromApi(data as Parameters<typeof mapCategoryFromApi>[0]) : (data as MerchantCategory);
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to create category');
  }
});

export const updateMerchantCategory = createAsyncThunk<
  MerchantCategory,
  { id: string; formData: FormData },
  { rejectValue: string }
>('merchantCategories/update', async ({ id, formData }, { rejectWithValue }) => {
  try {
    const response = await API.put<{ data?: MerchantCategory }>(`/merchant-categories/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const data = response.data?.data ?? response.data;
    return data ? mapCategoryFromApi(data as Parameters<typeof mapCategoryFromApi>[0]) : (data as MerchantCategory);
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to update category');
  }
});

export const deleteMerchantCategory = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('merchantCategories/delete', async (id, { rejectWithValue }) => {
  try {
    await API.delete(`/merchant-categories/${id}`);
    return id;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to delete category');
  }
});

export const reorderMerchantCategories = createAsyncThunk<
  { order: string[] },
  { categories: MerchantCategory[]; newOrder: string[] },
  { rejectValue: string }
>('merchantCategories/reorder', async ({ categories, newOrder }, { rejectWithValue }) => {
  try {
    const reordered = newOrder.map((id, index) => {
      const cat = categories.find((c) => c.id === id);
      return cat ? { ...cat, sequence_number: index + 1 } : null;
    }).filter(Boolean) as MerchantCategory[];
    await Promise.all(
      reordered.map((category) =>
        API.put(`/merchant-categories/${category.id}`, {
          id: category.id,
          category: category.category,
          sequence_number: category.sequence_number,
          items: category.items,
        })
      )
    );
    return { order: newOrder };
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string }; message?: string }; message?: string };
    return rejectWithValue(e.response?.data?.message || e.message || 'Failed to reorder');
  }
});

const merchantCategoriesSlice = createSlice({
  name: 'merchantCategories',
  initialState,
  reducers: {
    setCategoryOrder: (state, action: { payload: string[] }) => {
      state.categoryOrder = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMerchantsForCategories.pending, (state) => {
        state.loading.merchants = true;
        state.error = null;
      })
      .addCase(fetchMerchantsForCategories.fulfilled, (state, action) => {
        state.loading.merchants = false;
        state.merchants = action.payload;
      })
      .addCase(fetchMerchantsForCategories.rejected, (state, action) => {
        state.loading.merchants = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchMerchantCategories.pending, (state) => {
        state.loading.categories = true;
        state.error = null;
      })
      .addCase(fetchMerchantCategories.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.categories = action.payload;
        state.categoryOrder = action.payload.map((c) => c.id);
      })
      .addCase(fetchMerchantCategories.rejected, (state, action) => {
        state.loading.categories = false;
        state.error = action.payload ?? null;
      })
      .addCase(createMerchantCategory.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createMerchantCategory.fulfilled, (state) => {
        state.loading.create = false;
      })
      .addCase(createMerchantCategory.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateMerchantCategory.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateMerchantCategory.fulfilled, (state) => {
        state.loading.update = false;
      })
      .addCase(updateMerchantCategory.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteMerchantCategory.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteMerchantCategory.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.categories = state.categories.filter((c) => c.id !== action.payload);
        state.categoryOrder = state.categoryOrder.filter((id) => id !== action.payload);
      })
      .addCase(deleteMerchantCategory.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload ?? null;
      })
      .addCase(reorderMerchantCategories.pending, (state) => {
        state.loading.reorder = true;
        state.error = null;
      })
      .addCase(reorderMerchantCategories.fulfilled, (state, action) => {
        state.loading.reorder = false;
        if (action.payload.order) {
          state.categoryOrder = action.payload.order;
          action.payload.order.forEach((id, index) => {
            const cat = state.categories.find((c) => c.id === id);
            if (cat) cat.sequence_number = index + 1;
          });
        }
      })
      .addCase(reorderMerchantCategories.rejected, (state, action) => {
        state.loading.reorder = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { setCategoryOrder, clearError } = merchantCategoriesSlice.actions;
export default merchantCategoriesSlice.reducer;
